import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import { clock } from '@/lib/clock';
import { levelToScale } from '@/lib/micLevel';
import { barSession, subscribeBar } from '@/lib/recordingBar';

/**
 * Прилипшая полоска записи: «● Запись 0:47 · Стоп» и живая шкала уровня
 * (решение владельца §5.3, 12.09.2026).
 *
 * 🔴 ЗАЧЕМ. Задания в уроке длинные, и ученик прокручивает экран, читая вопросы.
 * Кнопка «Остановить» и часы уезжали наверх, а с ними — единственный признак
 * того, что запись вообще идёт. Полоска видна при любой прокрутке.
 *
 * ⚠️ ЧАСЫ И ШКАЛА РИСУЮТСЯ В КАДРЕ, А НЕ ЧЕРЕЗ СОСТОЯНИЕ REACT. Подписка здесь
 * дёргается только на смене состояния — началась запись, встала на паузу,
 * кончилась. Перерисовывать страницу урока шестьдесят раз в секунду ради бегущих
 * цифр — верный способ подтормаживать на телефоне.
 *
 * ⚠️ ЖИВЁТ В ОБОЛОЧКЕ ПРИЛОЖЕНИЯ, рядом с полосой связи и сообщением о звуке.
 * Портал не нужен: стор знает, идёт ли запись, а оболочка и так над всем.
 */
export function RecordingBar() {
  const { t } = useTranslation();
  const session = useSyncExternalStore(subscribeBar, barSession, barSession);
  const timeRef = useRef<HTMLSpanElement | null>(null);
  const levelRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!session) return;
    let alive = true;
    let frame = 0;
    const tick = () => {
      if (!alive) return;
      const time = timeRef.current;
      const bar = levelRef.current;
      if (time) time.textContent = clock(session.elapsedMs() / 1000);
      // По децибелам, как шкала в самом задании: линейная была еле заметной (14.09.2026).
      if (bar) bar.style.transform = `scaleX(${Math.max(0.02, levelToScale(session.level()))})`;
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      alive = false;
      cancelAnimationFrame(frame);
    };
  }, [session]);

  if (!session) return null;
  const paused = session.state() === 'paused';

  return (
    <div
      role="status"
      // Под полоской нативный ролик урока обрезается (ветка video-native-webview) — `lib/inlineVideoNative.ts`.
      data-native-video-cover=""
      className="safe-inset fixed inset-x-0 top-0 z-40 flex items-center gap-3 border-b border-outline bg-surface/95 px-4 py-2 backdrop-blur"
    >
      <span
        aria-hidden="true"
        className={`h-2.5 w-2.5 flex-none rounded-full bg-error ${paused ? '' : 'animate-pulse'}`}
      />
      <span className="flex-none text-[13px] font-semibold text-on-surface">
        {paused ? t('blockRenderers.audioPaused') : t('blockRenderers.recordBarLabel')}
      </span>
      <span ref={timeRef} className="w-12 flex-none text-[13px] font-bold tabular-nums text-on-surface">
        0:00
      </span>
      {/* Шкала уровня: ученик видит, что его слышно, а не только что время идёт. */}
      <span className="h-3 min-w-10 flex-1 overflow-hidden rounded-pill bg-surface-container-highest">
        <span
          ref={levelRef}
          className="block h-full origin-left rounded-pill bg-primary"
          style={{ transform: 'scaleX(0.02)' }}
        />
      </span>
      <button
        type="button"
        onClick={() => session.stop()}
        className="min-h-11 flex-none rounded-button bg-error px-4 text-[13px] font-bold text-on-error"
      >
        {t('blockRenderers.audioStop')}
      </button>
    </div>
  );
}
